import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowRight, ArrowLeft, Save, CheckCircle, Shield, User, Briefcase, Users,
  DollarSign, Car, PenLine, AlertCircle, Phone, X,
} from 'lucide-react';
import MainNav from '@/components/MainNav';
import SEO from '@/components/SEO';
import SignatureCanvas from '@/components/SignatureCanvas';
import { useLanguage } from '@/i18n/LanguageContext';
import { track } from '@/lib/analytics';

/**
 * Start a Bond — Full Bond Intake Questionnaire.
 *
 * This mirrors the Lone Star Bonding Co. "IntakePage" flow exactly:
 * landing (resume-or-new) -> consent -> 13-step guided questionnaire ->
 * review & sign -> submit -> success. On submit, the backend generates
 * 3 PDFs (Personal Reference Form, Surety Bond Application, Asset &
 * Collateral Inventory) via ReportLab and stores them in GridFS.
 * Same "TITN Aegis Security" verbiage + resume-by-SSN4/DOB mechanics.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOTAL_STEPS = 13;
const ABB_AGENT_NAME_DISPLAY = 'Steve Cruz';

const stepConfig = [
  { id: 1, icon: <Shield size={18} />, en: 'Consent & Identity', es: 'Consentimiento e Identidad' },
  { id: 2, icon: <User size={18} />, en: 'Your Personal Info', es: 'Información Personal' },
  { id: 3, icon: <User size={18} />, en: 'About the Defendant', es: 'Sobre el Acusado' },
  { id: 4, icon: <Briefcase size={18} />, en: 'Your Employment', es: 'Su Empleo' },
  { id: 5, icon: <Users size={18} />, en: 'Spouse / Partner', es: 'Cónyuge / Pareja' },
  { id: 6, icon: <Users size={18} />, en: 'Children', es: 'Hijos' },
  { id: 7, icon: <Users size={18} />, en: 'Family Contacts', es: 'Contactos Familiares' },
  { id: 8, icon: <Users size={18} />, en: 'Brothers & Sisters', es: 'Hermanos y Hermanas' },
  { id: 9, icon: <Users size={18} />, en: 'Other Relatives', es: 'Otros Familiares' },
  { id: 10, icon: <Users size={18} />, en: 'Personal References', es: 'Referencias Personales' },
  { id: 11, icon: <DollarSign size={18} />, en: 'Financial Info', es: 'Información Financiera' },
  { id: 12, icon: <Car size={18} />, en: 'Vehicles', es: 'Vehículos' },
  { id: 13, icon: <PenLine size={18} />, en: 'Review & Sign', es: 'Revisar y Firmar' },
];

/* ── Small shared field primitives (inline-styled, matching the exact
   Lone Star layout mechanics — visual polish will be finalized by the
   design/UI pass; structure + behavior must stay 1:1). ── */
const Field = ({ label, name, value, onChange, type = 'text', placeholder = '', required = false, half = false, options = null }) => (
  <div style={{ gridColumn: half ? 'span 1' : 'span 2' }}>
    <label className="form-label">{label}{required && <span style={{ color: 'var(--red-bright)', marginLeft: 3 }}>*</span>}</label>
    {options ? (
      <select name={name} value={value || ''} onChange={onChange} className="form-select">
        <option value="">— Select —</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    ) : (
      <input type={type} name={name} value={value || ''} onChange={onChange}
        className="form-input" placeholder={placeholder} required={required} />
    )}
  </div>
);

const SectionTitle = ({ children }) => (
  <div style={{ gridColumn: 'span 2', borderBottom: '1px solid var(--border-brass)', paddingBottom: '0.5rem', marginBottom: '0.25rem' }}>
    <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{children}</h3>
  </div>
);

const RepeaterGroup = ({ label, fields, items, onChange, count, lang }) => {
  const rows = Array.from({ length: count }).map((_, i) => items[i] || {});
  return (
    <div style={{ gridColumn: 'span 2' }}>
      <div style={{ borderBottom: '1px solid var(--border-dim)', paddingBottom: '0.4rem', marginBottom: '0.75rem' }}>
        <span style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
      </div>
      {rows.map((row, i) => (
        <div key={i} className="intake-repeater-row">
          <div style={{ gridColumn: 'span 3', display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
            <span className="intake-repeater-index">{i + 1}</span>
          </div>
          {fields.map((f) => (
            <div key={f.key}>
              <label className="form-label">{lang === 'es' ? f.labelEs : f.labelEn}</label>
              <input className="form-input" value={row[f.key] || ''} onChange={(e) => {
                const updated = [...(items.length > i ? items : [...items, ...Array.from({ length: i + 1 - items.length }).map(() => ({}))])];
                while (updated.length <= i) updated.push({});
                updated[i] = { ...updated[i], [f.key]: e.target.value };
                onChange(updated);
              }} placeholder={lang === 'es' ? f.labelEs : f.labelEn} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

const StartABond = () => {
  const { lang } = useLanguage();
  const [phase, setPhase] = useState('landing'); // landing | consent | form | success
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({});
  const [ssn4, setSsn4] = useState('');
  const [dob, setDob] = useState('');
  const [signature, setSignature] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [formId, setFormId] = useState(null);

  useEffect(() => { track('form_start', {}, '/start-a-bond'); }, []);

  const setField = useCallback((name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  // Auto-save draft every 15s while filling out the questionnaire.
  useEffect(() => {
    if (phase !== 'form' || !ssn4 || !dob) return;
    const timer = setInterval(async () => {
      try { await axios.post(`${API}/drafts`, { ssn_last4: ssn4, dob, step, data: formData }); } catch { /* silent */ }
    }, 15000);
    return () => clearInterval(timer);
  }, [phase, step, formData, ssn4, dob]);

  const handleResume = async () => {
    if (ssn4.length !== 4 || !dob) {
      setErrors({ resume: lang === 'es' ? 'Por favor ingresa los últimos 4 del SSN y fecha de nacimiento.' : 'Please enter last 4 of SSN and date of birth.' });
      return;
    }
    try {
      const res = await axios.get(`${API}/drafts/resume`, { params: { ssn_last4: ssn4, dob } });
      if (res.data.found) {
        setFormData(res.data.draft.data || {});
        setPhase('consent');
        track('application_resume', {}, '/start-a-bond');
        toast.success(lang === 'es' ? '¡Solicitud encontrada! Continuando...' : 'Application found! Continuing...');
      } else {
        setPhase('consent');
        toast(lang === 'es' ? 'No se encontró solicitud guardada. Iniciando nueva.' : 'No saved application found. Starting new.');
      }
    } catch { setPhase('consent'); }
  };

  const handleSave = async () => {
    try {
      await axios.post(`${API}/drafts`, { ssn_last4: ssn4, dob, step, data: formData });
      toast.success(lang === 'es' ? 'Progreso guardado' : 'Progress saved');
    } catch { toast.error(lang === 'es' ? 'No se pudo guardar' : 'Save failed'); }
  };

  const validate = () => {
    const errs = {};
    if (step === 2) {
      if (!formData.full_name) errs.full_name = true;
      if (!formData.telephone) errs.telephone = true;
    }
    if (step === 13 && !signature) errs.signature = true;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const nextStep = async () => {
    if (!validate()) {
      track('form_error', { step });
      toast.error(lang === 'es' ? 'Por favor completa los campos requeridos.' : 'Please complete required fields.');
      return;
    }
    track('form_step_complete', { step });
    if (step < TOTAL_STEPS) {
      setStep((s) => s + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      try { await axios.post(`${API}/drafts`, { ssn_last4: ssn4, dob, step: step + 1, data: formData }); } catch { /* silent */ }
    }
  };

  const prevStep = () => { if (step > 1) { setStep((s) => s - 1); window.scrollTo({ top: 0, behavior: 'smooth' }); } };

  const handleSubmit = async () => {
    if (!signature) { toast.error(lang === 'es' ? 'Se requiere firma.' : 'Signature is required.'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/submissions`, {
        ssn_last4: ssn4, dob, data: formData, client_signature: signature,
      });
      setFormId(res.data.form_id);
      setPhase('success');
      track('form_submit', { status: 'submitted' });
      track('application_completed', {});
      toast.success(lang === 'es' ? '¡Formulario enviado!' : 'Form submitted!');
    } catch (e) {
      track('form_error', { step: 13, message: String(e?.response?.data?.detail || '') });
      toast.error(lang === 'es' ? 'Error al enviar. Intente de nuevo o llámenos.' : 'Submission failed. Please try again or call us.');
    } finally { setSubmitting(false); }
  };

  const progress = ((step - 1) / (TOTAL_STEPS - 1)) * 100;

  /* ── LANDING ─────────────────────────────────────────── */
  if (phase === 'landing') {
    return (
      <div className="intake-wrap">
        <SEO title="Start a Bond | Amigo's Bail Bonds" description="Start your bail bond application — secure, guided, bilingual. Resume anytime with your last 4 SSN and date of birth." path="/start-a-bond" />
        <MainNav />
        <section className="intake-landing-section">
          <div className="container" style={{ maxWidth: 760 }}>
            <div style={{ textAlign: 'center' }}>
              <div className="intake-eyebrow-badge">
                <Shield size={13} color="var(--gold)" />
                <span>{lang === 'es' ? 'SEGURO — CONFIDENCIAL — GUIADO' : 'SECURE — CONFIDENTIAL — GUIDED'}</span>
              </div>
              <h1 className="section-title">
                {lang === 'es' ? 'Formularios de Solicitud de Fianza' : 'Bond Application Forms'}
              </h1>
              <p className="section-subtitle" style={{ margin: '0 auto 2.5rem', maxWidth: '58ch' }}>
                {lang === 'es'
                  ? 'Un cuestionario guiado paso a paso. Su información se ingresa una vez y se distribuye automáticamente a los formularios correctos.'
                  : 'One guided step-by-step questionnaire. Your information is entered once and automatically distributed to the correct forms.'}
              </p>
            </div>

            <div className="intake-option-grid">
              <div className="intake-option-card intake-option-card--primary">
                <h3>{lang === 'es' ? 'CONTINUAR SOLICITUD' : 'CONTINUE APPLICATION'}</h3>
                <p>{lang === 'es' ? 'Ingresa los últimos 4 del SSN y fecha de nacimiento para continuar.' : 'Enter your last 4 SSN and date of birth to resume.'}</p>
                <div className="form-field">
                  <label className="form-label" htmlFor="ssn4-resume">{lang === 'es' ? 'Últimos 4 del SSN' : 'Last 4 of SSN'}</label>
                  <input id="ssn4-resume" className="form-input" maxLength={4} value={ssn4} onChange={(e) => setSsn4(e.target.value.replace(/\D/g, ''))} placeholder="e.g. 1234" data-testid="ssn-input" />
                </div>
                <div className="form-field">
                  <label className="form-label" htmlFor="dob-resume">{lang === 'es' ? 'Fecha de Nacimiento' : 'Date of Birth'}</label>
                  <input id="dob-resume" className="form-input" type="date" value={dob} onChange={(e) => setDob(e.target.value)} data-testid="dob-input" />
                </div>
                {errors.resume && <div className="form-error">{errors.resume}</div>}
                <button onClick={handleResume} className="btn-red" style={{ width: '100%', justifyContent: 'center' }} data-testid="resume-btn">
                  {lang === 'es' ? 'CONTINUAR MI SOLICITUD' : 'RESUME MY APPLICATION'}
                </button>
              </div>

              <div className="intake-option-card">
                <h3 style={{ color: 'var(--text-primary)' }}>{lang === 'es' ? 'NUEVA SOLICITUD' : 'NEW APPLICATION'}</h3>
                <p>{lang === 'es' ? 'Inicia el proceso guiado desde el principio. Tomará aproximadamente 15–20 minutos.' : 'Start the guided process from the beginning. Takes approximately 15–20 minutes.'}</p>
                <ul className="intake-checklist">
                  {(lang === 'es'
                    ? ['Referencias Personales', 'Solicitud de Fianza', 'Inventario de Activos y Garantía']
                    : ['Personal References', 'Surety Bond Application', 'Asset & Collateral Inventory']
                  ).map((f, i) => (
                    <li key={i}><CheckCircle size={13} color="var(--success)" /> {f}</li>
                  ))}
                </ul>
                <div className="form-field">
                  <label className="form-label" htmlFor="ssn4-new">{lang === 'es' ? 'Sus Últimos 4 del SSN' : 'Your Last 4 of SSN'}</label>
                  <input id="ssn4-new" className="form-input" maxLength={4} value={ssn4} onChange={(e) => setSsn4(e.target.value.replace(/\D/g, ''))} placeholder="e.g. 1234" data-testid="ssn-input-new" />
                </div>
                <div className="form-field">
                  <label className="form-label" htmlFor="dob-new">{lang === 'es' ? 'Fecha de Nacimiento' : 'Date of Birth'}</label>
                  <input id="dob-new" className="form-input" type="date" value={dob} onChange={(e) => setDob(e.target.value)} data-testid="dob-input-new" />
                </div>
                <button
                  onClick={() => { if (!ssn4 || ssn4.length < 4 || !dob) { setErrors({ resume: lang === 'es' ? 'Requerido' : 'Required' }); return; } setPhase('consent'); }}
                  className="btn-brass" style={{ width: '100%', justifyContent: 'center' }} data-testid="new-app-btn">
                  {lang === 'es' ? 'INICIAR NUEVA SOLICITUD' : 'START NEW APPLICATION'} <ArrowRight size={14} />
                </button>
              </div>
            </div>

            <div className="intake-security-note">
              <Shield size={16} color="var(--success)" style={{ flexShrink: 0 }} />
              <span>{lang === 'es'
                ? 'Sus datos están protegidos por el Módulo de Seguridad TITN Aegis — encriptación personalizada de extremo a extremo desarrollada por James Hudson @ Texas IT Nerds.'
                : 'Your data is protected by the TITN Aegis Security Module — custom end-to-end encryption developed by James Hudson @ Texas IT Nerds.'}</span>
            </div>
          </div>
        </section>
      </div>
    );
  }

  /* ── CONSENT ─────────────────────────────────────────── */
  if (phase === 'consent') {
    return (
      <div className="intake-wrap">
        <SEO title="Data Consent | Amigo's Bail Bonds" description="Data consent and security agreement." path="/start-a-bond" />
        <MainNav />
        <section className="intake-consent-section">
          <div className="container" style={{ maxWidth: 700 }}>
            <div className="intake-consent-card">
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                <div className="intake-icon-badge"><Shield size={24} color="var(--gold)" /></div>
                <div>
                  <h2 className="wizard-title" style={{ margin: 0 }}>
                    {lang === 'es' ? 'CONSENTIMIENTO DE DATOS Y SEGURIDAD' : 'DATA CONSENT & SECURITY AGREEMENT'}
                  </h2>
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: 0 }}>
                    {lang === 'es' ? 'Lea antes de continuar' : 'Please read before continuing'}
                  </p>
                </div>
              </div>

              <div className="intake-consent-body">
                <p>
                  {lang === 'es'
                    ? "Al continuar, usted consiente que Amigo's Bail Bonds almacene de forma segura su información personal en nuestra plataforma. Todos los datos están protegidos por el "
                    : "By proceeding, you consent to Amigo's Bail Bonds securely storing your personal information on our platform. All data is protected by the "}
                  <strong style={{ color: 'var(--gold)' }}>TITN Aegis Security Module</strong>
                  {lang === 'es'
                    ? ' — un sistema de seguridad personalizado, completamente encriptado, diseñado y desarrollado internamente desde cero por James Hudson en Texas IT Nerds.'
                    : ' — a custom-developed, fully encrypted security system designed and built in-house from the ground up by James Hudson at Texas IT Nerds.'}
                </p>
                <p>
                  {lang === 'es'
                    ? "Su información está encriptada en reposo y en tránsito, y solo es accesible al personal autorizado de Amigo's Bail Bonds."
                    : "Your information is encrypted at rest and in transit, accessible only to authorized Amigo's Bail Bonds personnel."}
                </p>
                <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: 0 }}>
                  {lang === 'es'
                    ? "Esta información se utilizará exclusivamente para procesar su solicitud de fianza. Amigo's Bail Bonds | Licencia No. 0111"
                    : "This information will be used solely to process your bail bond application. Amigo's Bail Bonds | License No. 0111"}
                </p>
              </div>

              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <button onClick={() => { setStep(1); setPhase('form'); }} className="btn-red" style={{ flex: 1, justifyContent: 'center' }} data-testid="consent-agree-btn">
                  <CheckCircle size={16} /> {lang === 'es' ? 'ACEPTO — CONTINUAR' : 'I AGREE — PROCEED SECURELY'}
                </button>
                <button onClick={() => setPhase('landing')} className="btn-ghost-dark" data-testid="consent-cancel-btn">
                  <X size={14} /> {lang === 'es' ? 'Cancelar' : 'Cancel'}
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  /* ── SUCCESS ─────────────────────────────────────────── */
  if (phase === 'success') {
    return (
      <div className="intake-wrap intake-success-wrap">
        <SEO title="Submitted | Amigo's Bail Bonds" description="Your bond application forms were submitted successfully." path="/start-a-bond" />
        <div className="intake-success-card" data-testid="intake-success-card">
          <div className="intake-success-icon"><CheckCircle size={38} color="var(--success)" /></div>
          <h2 className="wizard-title" style={{ color: 'var(--success)', textAlign: 'center' }}>
            {lang === 'es' ? '¡ENVIADO CON ÉXITO!' : 'SUBMITTED SUCCESSFULLY!'}
          </h2>
          <p style={{ color: 'var(--text-secondary)', textAlign: 'center', marginBottom: '0.5rem', lineHeight: 1.7 }}>
            {lang === 'es' ? "Sus formularios han sido recibidos de forma segura por Amigo's Bail Bonds." : "Your forms have been securely received by Amigo's Bail Bonds."}
          </p>
          {formId && (
            <div className="intake-formid-pill" data-testid="intake-form-id">
              <span>{lang === 'es' ? 'ID de Formulario: ' : 'Form ID: '}</span>
              <strong>{formId}</strong>
            </div>
          )}
          <p style={{ fontSize: '0.88rem', color: 'var(--text-muted)', lineHeight: 1.6, textAlign: 'center' }}>
            {lang === 'es'
              ? "El equipo de Amigo's Bail Bonds revisará su solicitud y se comunicará con usted próximamente. Para ayuda inmediata, llame ahora."
              : "The Amigo's Bail Bonds team will review your submission and contact you shortly. For immediate help, call now."}
          </p>
          <a href="tel:9368282500" className="btn-red" style={{ justifyContent: 'center', width: '100%' }} data-testid="intake-success-call-btn">
            <Phone size={18} /> {lang === 'es' ? 'LLAMAR (936) 828-2500' : 'CALL (936) 828-2500'}
          </a>
        </div>
      </div>
    );
  }

  /* ── FORM ────────────────────────────────────────────── */
  const renderStep = () => {
    const gridStyle = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' };

    switch (step) {
      case 1:
        return (
          <div style={gridStyle}>
            <SectionTitle>{lang === 'es' ? 'Identificación del Declarante' : 'Your Identification (for form resume)'}</SectionTitle>
            <div className="intake-info-note" style={{ gridColumn: 'span 2' }}>
              <AlertCircle size={16} color="var(--gold)" style={{ marginTop: 2, flexShrink: 0 }} />
              <span>{lang === 'es' ? 'Sus últimos 4 del SSN y fecha de nacimiento son su clave de acceso para continuar esta solicitud más tarde si necesita parar y regresar.' : 'Your last 4 SSN and date of birth are your access key to continue this application later if you need to stop and return.'}</span>
            </div>
            <div>
              <label className="form-label">{lang === 'es' ? 'Sus últimos 4 del SSN' : 'Your Last 4 of SSN'} <span style={{ color: 'var(--red-bright)' }}>*</span></label>
              <input className="form-input" value={ssn4} readOnly style={{ opacity: 0.75 }} />
            </div>
            <div>
              <label className="form-label">{lang === 'es' ? 'Fecha de Nacimiento' : 'Date of Birth'} <span style={{ color: 'var(--red-bright)' }}>*</span></label>
              <input className="form-input" type="date" value={dob} readOnly style={{ opacity: 0.75 }} />
            </div>
          </div>
        );

      case 2:
        return (
          <div style={gridStyle}>
            <SectionTitle>{lang === 'es' ? 'Información Personal del Indemnizador' : 'Indemnitor Personal Information'}</SectionTitle>
            <Field label={lang === 'es' ? 'Nombre Completo (Apellido, Nombre, Inicial)' : 'Full Name (Last, First, MI)'} name="full_name" value={formData.full_name} onChange={handleChange} required placeholder="Last, First MI" />
            <Field label={lang === 'es' ? 'Apodo / Alias' : 'Nickname / Alias'} name="nickname_alias" value={formData.nickname_alias} onChange={handleChange} half />
            <Field label={lang === 'es' ? 'Calle / Número' : 'Street Address'} name="address_street" value={formData.address_street} onChange={handleChange} placeholder="123 Main St" />
            <Field label="Apt / Suite" name="address_apt" value={formData.address_apt} onChange={handleChange} half />
            <Field label={lang === 'es' ? 'Ciudad' : 'City'} name="address_city" value={formData.address_city} onChange={handleChange} half />
            <Field label={lang === 'es' ? 'Estado' : 'State'} name="address_state" value={formData.address_state} onChange={handleChange} half />
            <Field label="ZIP" name="address_zip" value={formData.address_zip} onChange={handleChange} half />
            <Field label={lang === 'es' ? '¿Cuánto tiempo en esta dirección?' : 'How Long at This Address?'} name="how_long_at_address" value={formData.how_long_at_address} onChange={handleChange} half />
            <Field label={lang === 'es' ? 'Hipotecaria / Dueño / Arrendatario' : 'Mortgage Co. / Landlord'} name="mortgage_co_landlord" value={formData.mortgage_co_landlord} onChange={handleChange} />
            <Field label={lang === 'es' ? '¿Cuánto tiempo?' : 'How Long?'} name="mortgage_how_long" value={formData.mortgage_how_long} onChange={handleChange} half />
            <Field label={lang === 'es' ? 'Dirección Anterior' : 'Previous Address'} name="previous_address" value={formData.previous_address} onChange={handleChange} />
            <Field label={lang === 'es' ? 'Teléfono / Celular' : 'Phone / Cell'} name="telephone" value={formData.telephone} onChange={handleChange} required type="tel" placeholder="(936) 555-0000" half />
            <Field label="Email" name="email" value={formData.email} onChange={handleChange} type="email" half />
            <Field label={lang === 'es' ? "Licencia de Conducir / ID #" : "Driver's License / ID #"} name="drivers_license" value={formData.drivers_license} onChange={handleChange} half />
            <Field label={lang === 'es' ? 'Otro ID' : 'Other ID'} name="other_id" value={formData.other_id} onChange={handleChange} half />
            <Field label={lang === 'es' ? 'Número de Seguro Social' : 'Social Security #'} name="ssn" value={formData.ssn} onChange={handleChange} type="password" placeholder="XXX-XX-XXXX" half />
            <Field label={lang === 'es' ? 'Fecha de Nacimiento' : 'Date of Birth'} name="dob" value={formData.dob || dob} onChange={handleChange} type="date" half />
            <Field label={lang === 'es' ? 'Edad' : 'Age'} name="age" value={formData.age} onChange={handleChange} half />
            <Field label={lang === 'es' ? 'Sexo' : 'Sex'} name="sex" value={formData.sex} onChange={handleChange} half options={[{ value: 'M', label: 'Male / Masculino' }, { value: 'F', label: 'Female / Femenino' }, { value: 'Other', label: 'Other' }]} />
            <Field label={lang === 'es' ? 'Peso' : 'Weight'} name="weight" value={formData.weight} onChange={handleChange} half placeholder="e.g. 175 lbs" />
            <Field label={lang === 'es' ? 'Estatura' : 'Height'} name="height" value={formData.height} onChange={handleChange} half placeholder="e.g. 5ft 10in" />
            <Field label={lang === 'es' ? 'Complexión' : 'Build'} name="build" value={formData.build} onChange={handleChange} half options={[{ value: 'Slim', label: 'Slim' }, { value: 'Medium', label: 'Medium' }, { value: 'Heavy', label: 'Heavy' }, { value: 'Athletic', label: 'Athletic' }]} />
            <Field label={lang === 'es' ? 'Raza' : 'Race'} name="race" value={formData.race} onChange={handleChange} half />
            <Field label={lang === 'es' ? 'Color de Cabello' : 'Hair Color'} name="hair_color" value={formData.hair_color} onChange={handleChange} half />
            <Field label={lang === 'es' ? 'Color de Ojos' : 'Eye Color'} name="eye_color" value={formData.eye_color} onChange={handleChange} half />
          </div>
        );

      case 3:
        return (
          <div style={gridStyle}>
            <SectionTitle>{lang === 'es' ? 'Información del Acusado' : 'About the Defendant'}</SectionTitle>
            <Field label={lang === 'es' ? 'Nombre del Acusado' : "Defendant's Full Name"} name="defendant_name" value={formData.defendant_name} onChange={handleChange} required />
            <Field label={lang === 'es' ? 'Relación con el Acusado' : 'Your Relation to Defendant'} name="relation_to_defendant" value={formData.relation_to_defendant} onChange={handleChange} required half options={[{ value: 'Spouse', label: 'Spouse' }, { value: 'Parent', label: 'Parent' }, { value: 'Sibling', label: 'Sibling' }, { value: 'Child', label: 'Child' }, { value: 'Friend', label: 'Friend' }, { value: 'Other', label: 'Other' }]} />
            <Field label={lang === 'es' ? 'Teléfono del Acusado' : "Defendant's Phone"} name="defendant_phone" value={formData.defendant_phone} onChange={handleChange} type="tel" half />
            <Field label={lang === 'es' ? 'Dirección del Acusado' : "Defendant's Address"} name="defendant_address" value={formData.defendant_address} onChange={handleChange} />
            <Field label={lang === 'es' ? 'Cargo / Delito' : 'Charge / Offense'} name="charge" value={formData.charge} onChange={handleChange} />
            <Field label="SPN #" name="spn" value={formData.spn} onChange={handleChange} half />
            <Field label={lang === 'es' ? 'Monto de la Fianza' : 'Bond Amount'} name="bond_amount" value={formData.bond_amount} onChange={handleChange} half placeholder="e.g. $5,000" />
            <Field label={lang === 'es' ? 'Número de Causa / Caso' : 'Cause / Case Number'} name="cause_number" value={formData.cause_number} onChange={handleChange} half />
            <Field label={lang === 'es' ? 'Nombre del Tribunal' : 'Court Name'} name="court_name" value={formData.court_name} onChange={handleChange} half />
            <Field label={lang === 'es' ? 'Agencia de Arresto' : 'Arresting Agency'} name="arresting_agency" value={formData.arresting_agency} onChange={handleChange} half />
            <Field label={lang === 'es' ? 'Cárcel / Instalación' : 'Jail / Facility'} name="jail_facility" value={formData.jail_facility} onChange={handleChange} half />
          </div>
        );

      case 4:
        return (
          <div style={gridStyle}>
            <SectionTitle>{lang === 'es' ? 'Empleo Actual' : 'Current Employment'}</SectionTitle>
            <Field label={lang === 'es' ? 'Empleador' : 'Employer'} name="employer" value={formData.employer} onChange={handleChange} />
            <Field label={lang === 'es' ? 'Ocupación' : 'Occupation'} name="occupation" value={formData.occupation} onChange={handleChange} half />
            <Field label={lang === 'es' ? 'Salario Mensual' : 'Monthly Salary'} name="monthly_salary" value={formData.monthly_salary} onChange={handleChange} half placeholder="$0,000" />
            <Field label={lang === 'es' ? 'Dirección del Empleador' : 'Employer Address'} name="employer_address" value={formData.employer_address} onChange={handleChange} />
            <Field label={lang === 'es' ? 'Teléfono del Empleador' : 'Employer Phone'} name="employer_phone" value={formData.employer_phone} onChange={handleChange} type="tel" half />
            <Field label={lang === 'es' ? '¿Cuánto tiempo?' : 'How Long?'} name="employer_how_long" value={formData.employer_how_long} onChange={handleChange} half />
            <SectionTitle>{lang === 'es' ? 'Empleo Anterior' : 'Former Employment'}</SectionTitle>
            <Field label={lang === 'es' ? 'Empleador Anterior' : 'Former Employer'} name="former_employer" value={formData.former_employer} onChange={handleChange} />
            <Field label={lang === 'es' ? 'Ocupación' : 'Occupation'} name="former_occupation" value={formData.former_occupation} onChange={handleChange} half />
            <Field label={lang === 'es' ? '¿Cuánto tiempo?' : 'How Long?'} name="former_how_long" value={formData.former_how_long} onChange={handleChange} half />
            <Field label={lang === 'es' ? 'Dirección Anterior del Empleador' : 'Former Employer Address'} name="former_employer_address" value={formData.former_employer_address} onChange={handleChange} />
            <Field label={lang === 'es' ? 'Teléfono' : 'Phone'} name="former_employer_phone" value={formData.former_employer_phone} onChange={handleChange} type="tel" half />
          </div>
        );

      case 5:
        return (
          <div style={gridStyle}>
            <SectionTitle>{lang === 'es' ? 'Empleo del Cónyuge / Pareja' : 'Spouse / Common Law Employment'}</SectionTitle>
            <Field label={lang === 'es' ? 'Nombre del Cónyuge / Pareja' : "Spouse / Common Law's Name"} name="spouse_name" value={formData.spouse_name} onChange={handleChange} />
            <Field label={lang === 'es' ? 'Dirección del Cónyuge' : "Spouse's Address"} name="spouse_address" value={formData.spouse_address} onChange={handleChange} />
            <Field label={lang === 'es' ? 'Empleador' : 'Employer'} name="spouse_employer" value={formData.spouse_employer} onChange={handleChange} half />
            <Field label={lang === 'es' ? 'Ocupación' : 'Occupation'} name="spouse_occupation" value={formData.spouse_occupation} onChange={handleChange} half />
            <Field label={lang === 'es' ? 'Dirección del Empleador' : 'Employer Address'} name="spouse_employer_address" value={formData.spouse_employer_address} onChange={handleChange} />
            <Field label={lang === 'es' ? 'Teléfono' : 'Phone'} name="spouse_employer_phone" value={formData.spouse_employer_phone} onChange={handleChange} type="tel" half />
            <Field label={lang === 'es' ? '¿Cuánto tiempo?' : 'How Long?'} name="spouse_employer_how_long" value={formData.spouse_employer_how_long} onChange={handleChange} half />
          </div>
        );

      case 6:
        return (
          <div style={{ display: 'grid', gap: '1rem' }}>
            <SectionTitle>{lang === 'es' ? 'Nombres de los Hijos y Escuelas' : "Children's Names and Schools Attended"}</SectionTitle>
            {[0, 1, 2].map((i) => (
              <div key={i} className="intake-repeater-row" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                <span className="intake-repeater-label">{lang === 'es' ? `Hijo ${i + 1}` : `Child ${i + 1}`}</span>
                <div><label className="form-label">{lang === 'es' ? 'Nombre' : 'Name'}</label><input className="form-input" value={(formData.children || [])[i]?.name || ''} onChange={(e) => { const c = [...(formData.children || [0, 1, 2].map(() => ({})))]; while (c.length <= i) c.push({}); c[i] = { ...c[i], name: e.target.value }; setField('children', c); }} /></div>
                <div><label className="form-label">{lang === 'es' ? 'Edad' : 'Age'}</label><input className="form-input" value={(formData.children || [])[i]?.age || ''} onChange={(e) => { const c = [...(formData.children || [0, 1, 2].map(() => ({})))]; while (c.length <= i) c.push({}); c[i] = { ...c[i], age: e.target.value }; setField('children', c); }} /></div>
                <div><label className="form-label">{lang === 'es' ? 'Escuela' : 'School'}</label><input className="form-input" value={(formData.children || [])[i]?.school || ''} onChange={(e) => { const c = [...(formData.children || [0, 1, 2].map(() => ({})))]; while (c.length <= i) c.push({}); c[i] = { ...c[i], school: e.target.value }; setField('children', c); }} /></div>
              </div>
            ))}
          </div>
        );

      case 7:
        return (
          <div style={{ display: 'grid', gap: '1.25rem' }}>
            <SectionTitle>{lang === 'es' ? "Padres / Referencias Personales Familiares" : "Parent's / Personal Family References"}</SectionTitle>
            {[
              { en: 'Mother', es: 'Madre', prefix: 'mother' },
              { en: 'Father', es: 'Padre', prefix: 'father' },
              { en: 'Mother-In-Law', es: 'Suegra', prefix: 'mother_in_law' },
              { en: 'Father-In-Law', es: 'Suegro', prefix: 'father_in_law' },
            ].map(({ en, es: esLabel, prefix }) => (
              <div key={prefix} className="intake-repeater-row" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                <span className="intake-repeater-label">{lang === 'es' ? esLabel : en}</span>
                <div><label className="form-label">{lang === 'es' ? 'Nombre' : 'Name'}</label><input className="form-input" value={formData[`${prefix}_name`] || ''} onChange={(e) => setField(`${prefix}_name`, e.target.value)} /></div>
                <div><label className="form-label">{lang === 'es' ? 'Teléfono' : 'Phone #'}</label><input className="form-input" type="tel" value={formData[`${prefix}_phone`] || ''} onChange={(e) => setField(`${prefix}_phone`, e.target.value)} /></div>
                <div><label className="form-label">{lang === 'es' ? 'Dirección' : 'Address'}</label><input className="form-input" value={formData[`${prefix}_address`] || ''} onChange={(e) => setField(`${prefix}_address`, e.target.value)} /></div>
              </div>
            ))}
          </div>
        );

      case 8:
        return (
          <div style={{ display: 'grid', gap: '1rem' }}>
            <RepeaterGroup
              label={lang === 'es' ? 'Hermanos y Hermanas (en el estado o fuera del estado)' : 'Brothers & Sisters (In State or Out of State)'}
              fields={[{ key: 'name', labelEn: 'Name', labelEs: 'Nombre' }, { key: 'phone', labelEn: 'Phone #', labelEs: 'Teléfono' }, { key: 'address', labelEn: 'Address', labelEs: 'Dirección' }]}
              items={formData.siblings || []} onChange={(val) => setField('siblings', val)} count={4} lang={lang}
            />
          </div>
        );

      case 9:
        return (
          <div style={{ display: 'grid', gap: '1rem' }}>
            <RepeaterGroup
              label={lang === 'es' ? 'Otros Familiares (en el estado o fuera del estado)' : 'Other Relatives (In State or Out of State)'}
              fields={[{ key: 'name', labelEn: 'Name', labelEs: 'Nombre' }, { key: 'phone', labelEn: 'Phone #', labelEs: 'Teléfono' }, { key: 'address', labelEn: 'Address', labelEs: 'Dirección' }]}
              items={formData.relatives || []} onChange={(val) => setField('relatives', val)} count={4} lang={lang}
            />
          </div>
        );

      case 10:
        return (
          <div style={{ display: 'grid', gap: '1rem' }}>
            <div className="intake-info-note">
              <AlertCircle size={16} color="var(--gold)" style={{ marginTop: 1, flexShrink: 0 }} />
              <span>{lang === 'es' ? 'Estas referencias se usarán en el Formulario de Referencias Personales Y en la Solicitud de Fianza. Ingrese amigos cercanos, compañeros de trabajo, etc. — no familiares.' : 'These references populate both the Personal Reference Form AND the Surety Bond Application. List close friends, coworkers, etc. — not family.'}</span>
            </div>
            <RepeaterGroup
              label={lang === 'es' ? 'Referencias Personales (Amigos, Compañeros de Trabajo, etc.) — 5 requeridas' : 'Personal References (Close Friends, Co-Workers, Teammates) — 5 required'}
              fields={[{ key: 'name', labelEn: 'Full Name', labelEs: 'Nombre Completo' }, { key: 'address', labelEn: 'Address', labelEs: 'Dirección' }, { key: 'phone', labelEn: 'Phone #', labelEs: 'Teléfono' }]}
              items={formData.references || []} onChange={(val) => setField('references', val)} count={5} lang={lang}
            />
          </div>
        );

      case 11:
        return (
          <div style={{ display: 'grid', gap: '1.25rem' }}>
            <SectionTitle>{lang === 'es' ? 'Tarjetas de Crédito' : 'Credit Cards'}</SectionTitle>
            {[0, 1, 2].map((i) => (
              <div key={i} className="intake-repeater-row" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                <span className="intake-repeater-label">{`Card ${i + 1}`}</span>
                {[{ k: 'name', en: 'Card Name', es: 'Nombre' }, { k: 'number', en: 'Card #', es: 'Número' }, { k: 'exp', en: 'Exp Date', es: 'Vencimiento' }].map((f) => (
                  <div key={f.k}><label className="form-label">{lang === 'es' ? f.es : f.en}</label>
                    <input className="form-input" value={(formData.credit_cards || [])[i]?.[f.k] || ''} onChange={(e) => { const cc = [...(formData.credit_cards || [{}, {}, {}])]; while (cc.length <= i) cc.push({}); cc[i] = { ...cc[i], [f.k]: e.target.value }; setField('credit_cards', cc); }} /></div>
                ))}
              </div>
            ))}
            <SectionTitle>{lang === 'es' ? 'Información Bancaria' : 'Banking Information'}</SectionTitle>
            <div style={gridStyle}>
              <div><label className="form-label">{lang === 'es' ? 'Nombre del Banco' : 'Bank Name'}</label><input className="form-input" value={formData.bank_name || ''} onChange={(e) => setField('bank_name', e.target.value)} /></div>
              <div><label className="form-label">{lang === 'es' ? 'Dirección / Sucursal' : 'Address / Branch'}</label><input className="form-input" value={formData.bank_address || ''} onChange={(e) => setField('bank_address', e.target.value)} /></div>
              <div><label className="form-label">{lang === 'es' ? 'Cuenta de Ahorros #' : 'Savings Account #'}</label><input className="form-input" value={formData.savings_account || ''} onChange={(e) => setField('savings_account', e.target.value)} /></div>
              <div><label className="form-label">{lang === 'es' ? 'Cuenta Corriente #' : 'Checking Account #'}</label><input className="form-input" value={formData.checking_account || ''} onChange={(e) => setField('checking_account', e.target.value)} /></div>
              <div><label className="form-label">{lang === 'es' ? 'Cooperativa de Crédito' : 'Credit Union'}</label><input className="form-input" value={formData.credit_union || ''} onChange={(e) => setField('credit_union', e.target.value)} /></div>
              <div><label className="form-label">{lang === 'es' ? 'Cuenta #' : 'Account #'}</label><input className="form-input" value={formData.credit_union_account || ''} onChange={(e) => setField('credit_union_account', e.target.value)} /></div>
              <div><label className="form-label">{lang === 'es' ? 'Acciones / Fondos Mutuos' : 'Stocks / Mutual Funds'}</label><input className="form-input" value={formData.stocks_mutual_funds || ''} onChange={(e) => setField('stocks_mutual_funds', e.target.value)} /></div>
              <div><label className="form-label">{lang === 'es' ? 'Cuenta #' : 'Account #'}</label><input className="form-input" value={formData.stocks_account || ''} onChange={(e) => setField('stocks_account', e.target.value)} /></div>
            </div>

            <SectionTitle>{lang === 'es' ? 'Bienes Raíces / Propiedad' : 'Real Property / Real Estate'}</SectionTitle>
            <div className="intake-info-note">{lang === 'es' ? 'Enumere cualquier propiedad inmueble que posea (casa, terreno, etc.). Esto se usa para el formulario de inventario de activos/colateral.' : 'List any real property you own (home, land, etc.). This is used for the asset/collateral inventory form.'}</div>
            {[0, 1].map((i) => (
              <div key={i} className="intake-repeater-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <span className="intake-repeater-label" style={{ gridColumn: 'span 2' }}>{lang === 'es' ? `Propiedad ${i + 1}` : `Property ${i + 1}`}</span>
                {[
                  { k: 'address', en: 'Property Address', es: 'Dirección de la Propiedad' },
                  { k: 'type', en: 'Type (Home/Land/Commercial)', es: 'Tipo (Casa/Terreno/Comercial)' },
                  { k: 'estimated_value', en: 'Estimated Value ($)', es: 'Valor Estimado ($)' },
                  { k: 'mortgage_balance', en: 'Mortgage Balance Owed ($)', es: 'Saldo de Hipoteca ($)' },
                  { k: 'equity', en: 'Estimated Equity ($)', es: 'Capital Estimado ($)' },
                  { k: 'mortgage_company', en: 'Mortgage Company', es: 'Compañía Hipotecaria' },
                ].map((f) => (
                  <div key={f.k}><label className="form-label">{lang === 'es' ? f.es : f.en}</label>
                    <input className="form-input" value={(formData.real_property || [])[i]?.[f.k] || ''} onChange={(e) => { const rp = [...(formData.real_property || [{}, {}])]; while (rp.length <= i) rp.push({}); rp[i] = { ...rp[i], [f.k]: e.target.value }; setField('real_property', rp); }} /></div>
                ))}
              </div>
            ))}

            <SectionTitle>{lang === 'es' ? 'Ingresos y Gastos Mensuales' : 'Monthly Income & Expenses'}</SectionTitle>
            <div style={gridStyle}>
              <div><label className="form-label">{lang === 'es' ? 'Ingreso Mensual Total ($)' : 'Total Monthly Income ($)'}</label><input className="form-input" value={formData.monthly_income || ''} onChange={(e) => setField('monthly_income', e.target.value)} placeholder="$0,000" /></div>
              <div><label className="form-label">{lang === 'es' ? 'Ingreso Mensual del Cónyuge ($)' : 'Spouse Monthly Income ($)'}</label><input className="form-input" value={formData.spouse_monthly_income || ''} onChange={(e) => setField('spouse_monthly_income', e.target.value)} placeholder="$0,000" /></div>
              <div><label className="form-label">{lang === 'es' ? 'Pago de Renta / Hipoteca ($)' : 'Rent / Mortgage Payment ($)'}</label><input className="form-input" value={formData.monthly_rent_mortgage || ''} onChange={(e) => setField('monthly_rent_mortgage', e.target.value)} placeholder="$0,000" /></div>
              <div><label className="form-label">{lang === 'es' ? 'Pagos de Auto ($)' : 'Car Payments ($)'}</label><input className="form-input" value={formData.monthly_car_payments || ''} onChange={(e) => setField('monthly_car_payments', e.target.value)} placeholder="$0,000" /></div>
              <div><label className="form-label">{lang === 'es' ? 'Servicios (Luz, Agua, Gas) ($)' : 'Utilities ($)'}</label><input className="form-input" value={formData.monthly_utilities || ''} onChange={(e) => setField('monthly_utilities', e.target.value)} placeholder="$0,000" /></div>
              <div><label className="form-label">{lang === 'es' ? 'Otros Gastos Mensuales ($)' : 'Other Monthly Expenses ($)'}</label><input className="form-input" value={formData.monthly_other_expenses || ''} onChange={(e) => setField('monthly_other_expenses', e.target.value)} placeholder="$0,000" /></div>
            </div>

            <SectionTitle>{lang === 'es' ? 'Deudas Pendientes' : 'Outstanding Debts'}</SectionTitle>
            {[0, 1, 2].map((i) => (
              <div key={i} className="intake-repeater-row" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                <span className="intake-repeater-label">{lang === 'es' ? `Deuda ${i + 1}` : `Debt ${i + 1}`}</span>
                {[{ k: 'creditor', en: 'Creditor Name', es: 'Nombre del Acreedor' }, { k: 'amount_owed', en: 'Amount Owed ($)', es: 'Monto Adeudado ($)' }, { k: 'monthly_payment', en: 'Monthly Payment ($)', es: 'Pago Mensual ($)' }].map((f) => (
                  <div key={f.k}><label className="form-label">{lang === 'es' ? f.es : f.en}</label>
                    <input className="form-input" value={(formData.outstanding_debts || [])[i]?.[f.k] || ''} onChange={(e) => { const d = [...(formData.outstanding_debts || [{}, {}, {}])]; while (d.length <= i) d.push({}); d[i] = { ...d[i], [f.k]: e.target.value }; setField('outstanding_debts', d); }} /></div>
                ))}
              </div>
            ))}

            <SectionTitle>{lang === 'es' ? 'Otra Propiedad Valiosa' : 'Other Valuable Property'}</SectionTitle>
            <div style={gridStyle}>
              <div style={{ gridColumn: 'span 2' }}><label className="form-label">{lang === 'es' ? 'Describa cualquier otra propiedad valiosa (joyas, electrónicos, herramientas, equipo, etc.)' : 'Describe any other valuable property (jewelry, electronics, tools, equipment, etc.)'}</label>
                <textarea className="form-textarea" rows={3} value={formData.other_valuable_property || ''} onChange={(e) => setField('other_valuable_property', e.target.value)} /></div>
              <div><label className="form-label">{lang === 'es' ? 'Valor Estimado Total ($)' : 'Estimated Total Value ($)'}</label><input className="form-input" value={formData.other_property_value || ''} onChange={(e) => setField('other_property_value', e.target.value)} placeholder="$0,000" /></div>
            </div>
          </div>
        );

      case 12:
        return (
          <div style={{ display: 'grid', gap: '1.25rem' }}>
            <SectionTitle>{lang === 'es' ? 'Lista de Vehículos' : 'List All Automobiles'}</SectionTitle>
            {[0, 1].map((i) => (
              <div key={i} className="intake-repeater-row" style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}>
                <span className="intake-repeater-label" style={{ gridColumn: 'span 5' }}>{lang === 'es' ? `Vehículo ${i + 1}` : `Vehicle ${i + 1}`}</span>
                {[{ k: 'year', en: 'Year', es: 'Año' }, { k: 'make', en: 'Make', es: 'Marca' }, { k: 'model', en: 'Model', es: 'Modelo' }, { k: 'color', en: 'Color', es: 'Color' }, { k: 'license', en: 'Lic. #', es: 'Placa #' }].map((f) => (
                  <div key={f.k}><label className="form-label">{lang === 'es' ? f.es : f.en}</label>
                    <input className="form-input" value={(formData.automobiles || [])[i]?.[f.k] || ''} onChange={(e) => { const a = [...(formData.automobiles || [{}, {}])]; while (a.length <= i) a.push({}); a[i] = { ...a[i], [f.k]: e.target.value }; setField('automobiles', a); }} /></div>
                ))}
              </div>
            ))}
            <SectionTitle>{lang === 'es' ? 'Acreedores de Vehículos' : 'Lienholders'}</SectionTitle>
            {[0, 1].map((i) => (
              <div key={i} className="intake-repeater-row" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                <span className="intake-repeater-label">{`Lienholder ${i + 1}`}</span>
                {[{ k: 'name', en: 'Lienholder', es: 'Acreedor' }, { k: 'ins_co', en: 'Ins. Co.', es: 'Aseguradora' }, { k: 'phone', en: 'Phone #', es: 'Teléfono' }].map((f) => (
                  <div key={f.k}><label className="form-label">{lang === 'es' ? f.es : f.en}</label>
                    <input className="form-input" value={(formData.lienholders || [])[i]?.[f.k] || ''} onChange={(e) => { const l = [...(formData.lienholders || [{}, {}])]; while (l.length <= i) l.push({}); l[i] = { ...l[i], [f.k]: e.target.value }; setField('lienholders', l); }} /></div>
                ))}
              </div>
            ))}
          </div>
        );

      case 13:
        return (
          <div style={{ display: 'grid', gap: '1.5rem' }}>
            <SectionTitle>{lang === 'es' ? 'Resumen de la Solicitud' : 'Application Summary'}</SectionTitle>
            <div className="intake-summary-grid">
              {[
                [lang === 'es' ? 'Nombre Completo' : 'Full Name', formData.full_name],
                [lang === 'es' ? 'Teléfono' : 'Phone', formData.telephone],
                [lang === 'es' ? 'Dirección' : 'Address', `${formData.address_street || ''} ${formData.address_city || ''}`.trim()],
                [lang === 'es' ? 'Acusado' : 'Defendant', formData.defendant_name],
                [lang === 'es' ? 'Cargo' : 'Charge', formData.charge],
                [lang === 'es' ? 'Monto Fianza' : 'Bond Amount', formData.bond_amount],
                [lang === 'es' ? 'Empleador' : 'Employer', formData.employer],
                [lang === 'es' ? 'Referencias' : 'References', `${(formData.references || []).filter((r) => r.name).length} provided`],
              ].map(([label, val], i) => val ? (
                <div key={i}>
                  <div className="intake-summary-label">{label}</div>
                  <div className="intake-summary-value">{val}</div>
                </div>
              ) : null)}
            </div>

            <div className="intake-legal-note">
              <strong>{lang === 'es' ? 'POR FAVOR LEA ANTES DE FIRMAR:' : 'PLEASE READ CAREFULLY BEFORE SIGNING:'}</strong>
              {lang === 'es'
                ? ' El Cliente garantiza que las declaraciones y respuestas anteriores son verdad sin ninguna reserva, y se hacen con el propósito de inducir al Fiador a convertirse en fiador. El Cliente renuncia a su derecho con respecto a la Ley de Privacidad y autoriza el uso de copias de este documento.'
                : " The Client hereby warrants that the foregoing declarations made and answers given are the truth without any reservation, and are made for the purpose of inducing the Surety to become surety. The Client hereby waives their right with respect to the Privacy Act and authorizes the use of copies of this document by Amigo's Bail Bonds and its duly authorized representatives."}
            </div>

            <div style={gridStyle}>
              <div style={{ gridColumn: 'span 2' }}>
                <SignatureCanvas label={lang === 'es' ? 'Firma del Cliente — X' : 'CLIENT SIGN HERE — X'} onSignatureChange={setSignature} required height={140} />
                {errors.signature && <div className="form-error">{lang === 'es' ? 'Se requiere firma' : 'Signature required'}</div>}
              </div>
              <div>
                <label className="form-label">{lang === 'es' ? 'Dirección Postal' : 'Mailing Address'}</label>
                <input className="form-input" value={formData.mailing_address || formData.address_street || ''} onChange={(e) => setField('mailing_address', e.target.value)} />
              </div>
              <div>
                <label className="form-label">{lang === 'es' ? 'Fecha' : 'Date'}</label>
                <input className="form-input" type="date" value={formData.sign_date || new Date().toISOString().slice(0, 10)} onChange={(e) => setField('sign_date', e.target.value)} />
              </div>
            </div>

            <div className="intake-security-note">
              <Shield size={15} color="var(--success)" style={{ flexShrink: 0 }} />
              {lang === 'es'
                ? 'Al enviar, se generarán 3 formularios PDF separados: Formulario de Referencias Personales, Solicitud de Fianza e Inventario de Activos.'
                : 'On submission, 3 separate PDF forms will be generated: Personal Reference Form, Surety Bond Application, and Asset/Collateral Inventory.'}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const currentStep = stepConfig[step - 1];

  return (
    <div className="intake-wrap">
      <SEO title="Bond Application | Amigo's Bail Bonds" description="Guided bond application questionnaire." path="/start-a-bond" />
      <MainNav />
      <div className="intake-form-section">
        <div className="intake-form-container">
          <div className="intake-form-header">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem', marginBottom: '0.75rem' }}>
              <div>
                <h1 className="wizard-title">{lang === 'es' ? 'Solicitud de Fianza' : 'Bond Application'}</h1>
                <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: 0 }}>
                  {lang === 'es' ? `Paso ${step} de ${TOTAL_STEPS}` : `Step ${step} of ${TOTAL_STEPS}`} — {lang === 'es' ? currentStep?.es : currentStep?.en}
                </p>
              </div>
              <button onClick={handleSave} className="btn-ghost-dark" style={{ fontSize: '0.82rem', padding: '0.45rem 0.9rem' }} data-testid="save-progress-btn">
                <Save size={13} /> {lang === 'es' ? 'Guardar' : 'Save'}
              </button>
            </div>

            <div className="progress-bar-track"><div className="progress-bar-fill" style={{ width: `${progress}%` }} /></div>

            <div className="intake-step-dots">
              {stepConfig.map((s, i) => (
                <div key={i} className={`intake-step-dot ${i < step ? 'done' : ''} ${i === step - 1 ? 'active' : ''}`} />
              ))}
            </div>
          </div>

          <div className="intake-form-card" key={step}>
            <div className="intake-form-card-header">
              <div className="intake-icon-badge intake-icon-badge--sm">{currentStep?.icon}</div>
              <h2 className="wizard-title" style={{ margin: 0, fontSize: '1.3rem' }}>{lang === 'es' ? currentStep?.es : currentStep?.en}</h2>
            </div>
            {renderStep()}
          </div>

          <div className="intake-form-nav">
            <button onClick={prevStep} disabled={step === 1} className="btn-ghost-dark" style={{ opacity: step === 1 ? 0.4 : 1 }} data-testid="prev-step-btn">
              <ArrowLeft size={15} /> {lang === 'es' ? 'Atrás' : 'Back'}
            </button>
            {step < TOTAL_STEPS ? (
              <button onClick={nextStep} className="btn-red" data-testid="next-step-btn">
                {lang === 'es' ? 'SIGUIENTE' : 'NEXT'} <ArrowRight size={15} />
              </button>
            ) : (
              <button onClick={handleSubmit} disabled={submitting} className="btn-red" style={{ padding: '0.9rem 2rem', opacity: submitting ? 0.7 : 1 }} data-testid="submit-form-btn">
                {submitting ? (lang === 'es' ? 'ENVIANDO...' : 'SUBMITTING...') : (<><CheckCircle size={16} /> {lang === 'es' ? 'ENVIAR FORMULARIOS' : 'SUBMIT FORMS'}</>)}
              </button>
            )}
          </div>

          <p className="intake-license-note">
            Amigo&apos;s Bail Bonds | {ABB_AGENT_NAME_DISPLAY} | License No. 0111 | Conroe, TX 77301 · Montgomery County | (936) 828-2500
          </p>
        </div>
      </div>
    </div>
  );
};

export default StartABond;
